import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetJobApplicantsQuery, useGetJobOpeningDetailsQuery } from '../../../../utils/api_service/jobOpeningsApi';
import { jobOpeningDetailsTabs } from '../../../../utils/types/JobOpenings';

export const useJobOpeningData = () => {
  const { id } = useParams<{ id: string }>();

  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useGetJobOpeningDetailsQuery(id || '', {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });
  const { data: applicantsData, refetch: refetchApplicants } = useGetJobApplicantsQuery(
    { id: id || '', page: 1, limit: 1, status: '' },
    { skip: !id, refetchOnMountOrArgChange: true },
  );

  const completedTabs = useMemo(() => {
    if (!data) return [];
    const completedList: jobOpeningDetailsTabs[] = [];

    // Check if sections are completed based on existence and status flags
    if (data.jobRequisition) completedList.push('Job Requisition');

    // Job Description can be completed if status says so OR the internal flag is set
    if (data.status?.jobDescription === 'Completed' || data.jobDescription?.completed) {
      completedList.push('Job Description');
    }

    if (data.autoSuitability) completedList.push('Auto Suitability');
    if (data.autoOutstanding) completedList.push('Auto Outstanding');
    if (data.autoAssessment && (data.autoAssessment.id || data.autoAssessment.assessment?.id)) {
      completedList.push('Auto Assess Questions');
    }
    if (data.jobPosting) completedList.push('Job Posting');
    const isCVIngestionDone = (status?: string) => {
      if (!status) return false;
      const s = status.toLowerCase().replace(/\s/g, '');
      return s === 'completed' || s === 'inprogress';
    };

    if ((data.CVIngestion || isCVIngestionDone(data.status?.shortlisting)) && (applicantsData?.totalApplicants ?? 0) > 0) {
      completedList.push('CV Ingestion');
    }

    return completedList;
  }, [data, applicantsData]);

  const jobOpeningElements = useMemo(() => {
    return data?.jobOpeningElements || [];
  }, [data]);

  const handleRefetch = useCallback(() => {
    if (!id) return;
    refetch();
    refetchApplicants();
  }, [id, refetch, refetchApplicants]);

  const handleRefetchListing = useCallback(() => {
    if (!id) return;
    refetch();
  }, [id, refetch]);

  const handleRefetchApplicants = useCallback(() => {
    if (!id) return;
    refetchApplicants();
  }, [id, refetchApplicants]);

  const tabCounts = useMemo(() => {
    return {
      'Applicant Analysis':
        applicantsData?.totalApplicants ??
        applicantsData?.totalCount ??
        applicantsData?.total ??
        applicantsData?.count ??
        (Array.isArray(applicantsData?.jobApplicants) ? applicantsData.jobApplicants.length : 0),
      'Auto-Assessments':
        data?.autoAssessmentCount ??
        data?.autoAssessmentsCount ??
        (Array.isArray(data?.autoAssessment?.questions) ? data.autoAssessment.questions.length : 0),
      'Smart Interviews':
        data?.interviewsCount ??
        data?.smartInterviewsCount ??
        (Array.isArray(data?.interviews) ? data.interviews.length : 0),
      Psychometric:
        data?.psychometricCount ??
        (Array.isArray(data?.psychometricResults) ? data.psychometricResults.length : 0),
      Wellness:
        data?.wellnessCount ??
        (Array.isArray(data?.wellnessResults) ? data.wellnessResults.length : 0),
    };
  }, [data, applicantsData]);

  return {
    jobData: data,
    isLoading,
    isError,
    refetchListing: handleRefetchListing,
    refetchJobApplicants: handleRefetchApplicants,
    refetch: handleRefetch,
    jobTitle: data?.jobDescription?.jobTitle?.jobTitle ||
              data?.jobPosting?.jobTitle ||
              data?.autoAssessment?.assessment?.jobPosition ||
              '',
    jobOpeningElements,
    completedTabs,
    tabCounts,
  };
};
